import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, ChevronUp, Info, Shield, TrendingUp, Building2, Users, Layers, Crosshair } from "lucide-react";
import { openCalendly } from "@/lib/calendly";

// ═══════════════════════════════════════════════════════
// LAYER A — Official 2026 German Tax Engine (§32a EStG)
// ═══════════════════════════════════════════════════════

const GRUNDFREIBETRAG = 12348;
const ZONE1_END = 17799;
const ZONE2_END = 69878;
const ZONE3_END = 277825;
const SOLI_THRESHOLD_SINGLE = 20350;
const SOLI_THRESHOLD_MARRIED = 40700;
const KINDERFREIBETRAG_2026 = 6826; // per child
const BEA_FREIBETRAG = 2928; // Betreuungs-/Erziehungs-/Ausbildungsbedarf per child

const CHURCH_TAX_STATES = {
  "Baden-Württemberg": 0.08,
  "Bayern": 0.08,
  "Berlin": 0.09,
  "Brandenburg": 0.09,
  "Bremen": 0.09,
  "Hamburg": 0.09,
  "Hessen": 0.09,
  "Mecklenburg-Vorpommern": 0.09,
  "Niedersachsen": 0.09,
  "Nordrhein-Westfalen": 0.09,
  "Rheinland-Pfalz": 0.09,
  "Saarland": 0.09,
  "Sachsen": 0.09,
  "Sachsen-Anhalt": 0.09,
  "Schleswig-Holstein": 0.09,
  "Thüringen": 0.09,
};

function calcIncomeTax2026(zvE) {
  if (zvE <= GRUNDFREIBETRAG) return 0;
  if (zvE <= ZONE1_END) {
    const y = (zvE - GRUNDFREIBETRAG) / 10000;
    return Math.round((914.60 * y + 1400) * y);
  }
  if (zvE <= ZONE2_END) {
    const z = (zvE - ZONE1_END) / 10000;
    return Math.round((173.14 * z + 2397) * z + 1034.73);
  }
  if (zvE <= ZONE3_END) {
    return Math.round(0.42 * zvE - 11138.82);
  }
  return Math.round(0.45 * zvE - 19473.60);
}

function calcSoli2026(incomeTax, isMar, children) {
  const threshold = isMar ? SOLI_THRESHOLD_MARRIED : SOLI_THRESHOLD_SINGLE;
  // Kinderfreibetrag affects Soli calculation
  const childDeduction = children * (KINDERFREIBETRAG_2026 + BEA_FREIBETRAG) * (isMar ? 1 : 0.5);
  const adjustedTax = Math.max(0, incomeTax - (childDeduction > 0 ? childDeduction * 0.42 : 0));
  if (adjustedTax <= threshold) return 0;
  const overThreshold = adjustedTax - threshold;
  const fullSoli = adjustedTax * 0.055;
  const cappedSoli = overThreshold * 0.119;
  return Math.round(Math.min(fullSoli, cappedSoli));
}

// ═══════════════════════════════════════════════════════
// LAYER B — Official PAP 2026 Vorsorgepauschale + Social
// Sources: BMF PAP 2026 (Anlage 1), Bundesregierung,
// GKV-Spitzenverband, DRV, BA, BMG
// ═══════════════════════════════════════════════════════

// 2026 Beitragsbemessungsgrenzen (official Bundesregierung)
const BBG_RV = 101400;      // Renten- & Arbeitslosenversicherung (West)
const BBG_KV = 69750;       // Kranken- & Pflegeversicherung

// 2026 Beitragssätze — full rate (Gesamtbeitrag)
const RATE_RV_TOTAL = 0.186;    // Rentenversicherung
const RATE_KV_TOTAL = 0.146;    // Krankenversicherung (allgemeiner Beitragssatz)
const RATE_KV_ZUSATZ = 0.029;   // Durchschnittlicher Zusatzbeitrag 2026
const RATE_ALV_TOTAL = 0.026;   // Arbeitslosenversicherung
const RATE_PV_TOTAL = 0.036;    // Pflegeversicherung (Basissatz mit 1 Kind)

// Employee shares (Arbeitnehmeranteil = half of total)
const AN_RV = RATE_RV_TOTAL / 2;   // 0.093
const AN_KV = RATE_KV_TOTAL / 2;   // 0.073
const AN_KV_Z = RATE_KV_ZUSATZ / 2; // 0.0145
const AN_ALV = RATE_ALV_TOTAL / 2; // 0.013

// Pflegeversicherung 2026 employee rates by children (BMG official)
// Kinderloser Zuschlag: +0.6% → employee pays 2.4%
// 1 Kind: 1.8%, 2 Kinder: 1.55%, 3 Kinder: 1.30%, 4 Kinder: 1.05%, 5+ Kinder: 0.80%
function getPVRate(children) {
  if (children === 0) return 0.024;
  if (children === 1) return 0.018;
  if (children === 2) return 0.0155;
  if (children === 3) return 0.013;
  if (children === 4) return 0.0105;
  return 0.008;
}

// Werbungskostenpauschale, Sonderausgaben-Pauschbetrag (BMF)
const WERBUNGSKOSTEN = 1230;
const SONDERAUSGABEN_SINGLE = 36;
const SONDERAUSGABEN_MARRIED = 72;

// Calculate employee social contributions (actual payroll deductions)
function calcSocialContributions(gross, employmentType, children) {
  if (employmentType === "self-employed") {
    // Self-employed: voluntary GKV (full rate) + care, no pension/unemployment mandatory
    const kvBase = Math.min(gross, BBG_KV);
    const health = Math.round(kvBase * (RATE_KV_TOTAL + RATE_KV_ZUSATZ));
    const pvRate = children === 0 ? RATE_PV_TOTAL + 0.006 : RATE_PV_TOTAL;
    const care = Math.round(kvBase * pvRate);
    return { pension: 0, health, unemployment: 0, care, total: health + care };
  }
  const pension = Math.round(Math.min(gross, BBG_RV) * AN_RV);
  const health = Math.round(Math.min(gross, BBG_KV) * (AN_KV + AN_KV_Z));
  const unemployment = Math.round(Math.min(gross, BBG_RV) * AN_ALV);
  const pvRate = getPVRate(children);
  const care = Math.round(Math.min(gross, BBG_KV) * pvRate);
  return { pension, health, unemployment, care, total: pension + health + unemployment + care };
}

// PAP 2026 Vorsorgepauschale (simplified from BMF PAP Anlage 1)
// This determines how much of social contributions reduces taxable income for INCOME TAX
function calcVorsorgepauschale(gross, social, employmentType, isMarried) {
  if (employmentType === "self-employed") {
    // Self-employed: Vorsorgeaufwendungen via Steuererklärung, simplified here
    // Pension: Basisvorsorge fully deductible from 2025
    // Health: Basisbeiträge deductible
    return social.health + social.care;
  }

  // PAP 2026 Vorsorgepauschale components:
  // 1. VSPR: Rentenversicherung = AN-Beitrag (full from 2025 onwards)
  const vspr = social.pension;

  // 2. VSPKVPV: Kranken- + Pflegeversicherung
  // Uses employer-side rates applied to gross (capped at BBG_KV)
  // AG-Anteil KV: 7.3% + half of Zusatzbeitrag
  // AG-Anteil PV: 1.8% (base)
  const kvBase = Math.min(gross, BBG_KV);
  const agKV = kvBase * (0.073 + AN_KV_Z); // employer health contribution
  const agPV = kvBase * 0.018; // employer care contribution (base)
  const vspkvpv = Math.round(agKV + agPV);

  // 3. VSPALV: Arbeitslosenversicherung (new from 2026)
  // Only included if sum of VSPALV + VSPKVPV <= 1900
  const vspalv = social.unemployment;
  let vspkvpvAlv = vspkvpv;
  if (vspkvpv + vspalv <= 1900) {
    vspkvpvAlv = vspkvpv + vspalv;
  }

  // Total Vorsorgepauschale
  return vspr + vspkvpvAlv;
}

function calcTotalTax(grossIncome, spouseIncome, options, precision) {
  const { isMarried, isTwoIncome, churchTax, churchState, employmentType } = options;
  const { deductions, bonus, children } = precision;

  const totalGross = grossIncome + bonus;
  const spouseTotal = isTwoIncome ? spouseIncome : 0;

  // Actual social contributions (what you see on payslip)
  const social1 = calcSocialContributions(totalGross, employmentType, children);
  const social2 = isTwoIncome
    ? calcSocialContributions(spouseTotal, employmentType, children)
    : { total: 0, pension: 0, health: 0, unemployment: 0, care: 0 };

  // Vorsorgepauschale (what reduces taxable income for tax calculation)
  const vsp1 = calcVorsorgepauschale(totalGross, social1, employmentType, isMarried);
  const vsp2 = isTwoIncome ? calcVorsorgepauschale(spouseTotal, social2, employmentType, isMarried) : 0;

  const sonderausgaben = isMarried ? SONDERAUSGABEN_MARRIED : SONDERAUSGABEN_SINGLE;

  // zvE = gross - Vorsorgepauschale - Werbungskosten - Sonderausgaben - additional deductions
  let zvE1 = Math.max(0, totalGross - vsp1 - WERBUNGSKOSTEN - sonderausgaben - deductions);
  let zvE2 = isTwoIncome ? Math.max(0, spouseTotal - vsp2 - WERBUNGSKOSTEN - sonderausgaben) : 0;

  let incomeTax = 0;
  if (isMarried) {
    const combinedZvE = zvE1 + zvE2;
    const halfZvE = combinedZvE / 2;
    incomeTax = calcIncomeTax2026(halfZvE) * 2;
  } else {
    incomeTax = calcIncomeTax2026(zvE1);
  }

  const soli = calcSoli2026(incomeTax, isMarried, children);

  let churchTaxAmount = 0;
  if (churchTax && churchState) {
    const rate = CHURCH_TAX_STATES[churchState] || 0.09;
    const childReduction = children * (KINDERFREIBETRAG_2026 + BEA_FREIBETRAG) * (isMarried ? 1 : 0.5) * rate;
    churchTaxAmount = Math.max(0, Math.round(incomeTax * rate - childReduction));
  }

  const totalSocial = social1.total + social2.total;
  const totalTax = incomeTax + soli + churchTaxAmount + totalSocial;

  return {
    incomeTax,
    soli,
    churchTaxAmount,
    socialContributions: totalSocial,
    socialBreakdown: {
      pension: social1.pension + social2.pension,
      health: social1.health + social2.health,
      unemployment: social1.unemployment + social2.unemployment,
      care: social1.care + social2.care,
    },
    totalTax,
    zvE: isMarried ? zvE1 + zvE2 : zvE1,
  };
}

// ═══════════════════════════════════════════════════════
// LAYER C — IRE Strategy Engine
// ═══════════════════════════════════════════════════════

const SCENARIO_BASE = { conservative: 0.22, typical: 0.38, strong: 0.55 };
const SCENARIO_CAPS = { conservative: 0.30, typical: 0.48, strong: 0.68 };
const SCENARIO_FLOORS = { conservative: 5000, typical: 8000, strong: 12000 };

function calcRecovery(totalTax, grossIncome, spouseIncome, scenario, options, precision) {
  const { isMarried, isTwoIncome, employmentType } = options;
  const { equity, existingProperties } = precision;

  let factor = SCENARIO_BASE[scenario];

  // Income modifier
  if (grossIncome >= 220000) factor += 0.05;
  else if (grossIncome >= 160000) factor += 0.03;
  else if (grossIncome >= 110000) factor += 0.00;
  else if (grossIncome >= 80000) factor -= 0.02;

  // Household modifier
  if (isMarried && isTwoIncome && spouseIncome >= 80000) factor += 0.03;

  // Employment modifier
  if (employmentType === "self-employed") factor += 0.02;

  // Precision Mode modifiers
  // Equity modifier — higher equity enables stronger structures
  if (equity >= 150000) factor += 0.04;
  else if (equity >= 100000) factor += 0.03;
  else if (equity >= 50000) factor += 0.01;

  // Existing properties modifier — experienced investors access stronger structures
  if (existingProperties >= 3) factor += 0.04;
  else if (existingProperties >= 1) factor += 0.02;

  // Cap
  factor = Math.min(factor, SCENARIO_CAPS[scenario]);

  let recovery = Math.round(totalTax * factor);

  // Floor
  recovery = Math.max(recovery, SCENARIO_FLOORS[scenario]);

  // Don't exceed total tax
  recovery = Math.min(recovery, totalTax);

  const ratio = totalTax > 0 ? ((recovery / totalTax) * 100).toFixed(1) : "0.0";

  return { recovery, ratio, factor };
}

// ═══════════════════════════════════════════════════════
// Strategic-Fit Engine (Enhanced with Precision Mode)
// ═══════════════════════════════════════════════════════

function getStrategicFit(grossIncome, spouseIncome, totalTax, recovery, ratio, options, precision) {
  const { isMarried, isTwoIncome } = options;
  const { equity, existingProperties, goalType } = precision;
  const ratioNum = parseFloat(ratio);

  // Priority 0: Portfolio Builder if existing properties signal
  if (existingProperties >= 2 || (existingProperties >= 1 && equity >= 100000)) {
    return {
      label: "Portfolio Builder",
      icon: Layers,
      description: "Your profile suggests that the bigger opportunity may lie in sequencing multiple moves over time rather than optimizing a single acquisition in isolation.",
    };
  }

  // Priority 1: Household structure
  if (isMarried && !isTwoIncome) {
    return {
      label: "Household Strategy",
      icon: Users,
      description: "Your profile suggests that household structure and long-term sequencing matter as much as pure tax reduction.",
    };
  }
  if (isMarried && isTwoIncome && Math.abs(grossIncome - spouseIncome) > 60000) {
    return {
      label: "Household Strategy",
      icon: Users,
      description: "Your profile suggests that household structure and long-term sequencing matter as much as pure tax reduction.",
    };
  }

  // Priority 2: High tax + high ratio → Entry + Exit
  if (totalTax > 40000 && ratioNum > 40) {
    return {
      label: "Entry + Exit",
      icon: TrendingUp,
      description: "Your profile suggests a stronger fit for a more tax-intensive structure with faster recovery effects and higher strategic velocity.",
    };
  }

  // Priority 3: High tax + meaningful ratio → New-Build Anchor
  if (totalTax > 25000 && ratioNum > 25) {
    return {
      label: "New-Build Anchor",
      icon: Building2,
      description: "Your profile points toward a tax-efficient, low-complexity first move with strong financing quality and long-term stability.",
    };
  }

  // Priority 4: Moderate burden → Entry + Hold
  if (totalTax > 15000 && ratioNum <= 35) {
    return {
      label: "Entry + Hold",
      icon: Shield,
      description: "Your profile fits a long-term ownership strategy focused on stable asset build-up, structural simplicity, and tax efficiency over time.",
    };
  }

  // Priority 5: Portfolio Builder (high income)
  if (grossIncome > 200000) {
    return {
      label: "Portfolio Builder",
      icon: Layers,
      description: "Your profile suggests that the bigger opportunity may lie in sequencing multiple moves over time rather than optimizing a single acquisition in isolation.",
    };
  }

  // Default
  return {
    label: "New-Build Anchor",
    icon: Building2,
    description: "Your profile points toward a tax-efficient, low-complexity first move with strong financing quality and long-term stability.",
  };
}

// ═══════════════════════════════════════════════════════
// UI Component
// ═══════════════════════════════════════════════════════

const fmt = (n) => "€" + Math.round(n).toLocaleString("de-DE");

const CalculatorV2 = () => {
  const [household, setHousehold] = useState("single");
  const [incomeStructure, setIncomeStructure] = useState("one-income");
  const [grossIncome, setGrossIncome] = useState(120000);
  const [spouseIncome, setSpouseIncome] = useState(60000);
  const [employmentType, setEmploymentType] = useState("employee");
  const [churchTax, setChurchTax] = useState(false);
  const [churchState, setChurchState] = useState("Berlin");
  const [scenario, setScenario] = useState("typical");
  const [showAssumptions, setShowAssumptions] = useState(false);

  // ─── Precision Mode State ───
  const [precisionMode, setPrecisionMode] = useState(false);
  const [deductions, setDeductions] = useState(0);
  const [bonus, setBonus] = useState(0);
  const [children, setChildren] = useState(0);
  const [equity, setEquity] = useState(0);
  const [existingProperties, setExistingProperties] = useState(0);
  const [goalType, setGoalType] = useState("tax-first");

  const isMarried = household === "married";
  const isTwoIncome = incomeStructure === "two-income";

  const precisionData = useMemo(() => ({
    deductions: precisionMode ? deductions : 0,
    bonus: precisionMode ? bonus : 0,
    children: precisionMode ? children : 0,
    equity: precisionMode ? equity : 0,
    existingProperties: precisionMode ? existingProperties : 0,
    goalType: precisionMode ? goalType : "tax-first",
  }), [precisionMode, deductions, bonus, children, equity, existingProperties, goalType]);

  // ─── Calculations ───
  const taxResult = useMemo(() => {
    return calcTotalTax(grossIncome, isMarried && isTwoIncome ? spouseIncome : 0, {
      isMarried, isTwoIncome, churchTax, churchState, employmentType,
    }, precisionData);
  }, [grossIncome, spouseIncome, isMarried, isTwoIncome, churchTax, churchState, employmentType, precisionData]);

  const recoveryResult = useMemo(() => {
    return calcRecovery(taxResult.totalTax, grossIncome, spouseIncome, scenario, {
      isMarried, isTwoIncome, employmentType,
    }, precisionData);
  }, [taxResult.totalTax, grossIncome, spouseIncome, scenario, isMarried, isTwoIncome, employmentType, precisionData]);

  const strategicFit = useMemo(() => {
    return getStrategicFit(grossIncome, spouseIncome, taxResult.totalTax, recoveryResult.recovery, recoveryResult.ratio, {
      isMarried, isTwoIncome,
    }, precisionData);
  }, [grossIncome, spouseIncome, taxResult.totalTax, recoveryResult.recovery, recoveryResult.ratio, isMarried, isTwoIncome, precisionData]);

  const effectiveRate = grossIncome > 0 ? ((taxResult.totalTax / (grossIncome + (isMarried && isTwoIncome ? spouseIncome : 0))) * 100).toFixed(1) : "0.0";

  const FitIcon = strategicFit.icon;

  const scenarioDescriptions = {
    conservative: "Lower-intensity first-year tax effect with a cleaner and more conservative structure.",
    typical: "The most representative IRE-style scenario for a strong but realistic first-year tax effect.",
    strong: "A more tax-intensive structure with above-average first-year recovery potential.",
  };

  // Active button styles
  const btnOn = "bg-ire-accent/15 border-ire-accent/50 text-ire-accent shadow-[0_0_20px_rgba(212,175,106,0.15),inset_0_1px_0_rgba(212,175,106,0.1)]";
  const btnOff = "bg-transparent border-white/[0.08] text-white/45 hover:border-white/15 hover:text-white/50";

  return (
    <section className="min-h-screen bg-ire-black pt-24 sm:pt-32 pb-16 sm:pb-20 relative overflow-hidden" data-testid="calculator-v2">
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none" style={{ backgroundImage: `linear-gradient(rgba(212,175,106,1) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,106,1) 1px, transparent 1px)`, backgroundSize: "60px 60px" }} />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 md:px-16 lg:px-24 relative z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-16 bg-ire-accent" />
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">Tax-to-Asset Strategy Calculator</span>
            <div className="h-px flex-1 bg-gradient-to-r from-ire-accent/20 to-transparent" />
          </div>
          <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extralight text-white leading-[1.1] mb-4">Your 2026 tax picture.<br /><span className="text-ire-accent">Reinterpreted.</span></h1>
          <p className="font-body text-white/40 max-w-2xl text-sm sm:text-base leading-relaxed">Adjust income, household status, and tax setup to see your estimated 2026 tax burden — and how an IRE-style property strategy could change the picture.</p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12">
          {/* LEFT — Inputs */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="space-y-5">
            {/* Household */}
            <div className="relative bg-white/[0.015] border border-white/[0.08] p-4 sm:p-6 space-y-5" data-testid="household-setup">
              <div className="absolute top-0 left-0 w-8 h-px bg-ire-accent/40" /><div className="absolute top-0 left-0 w-px h-8 bg-ire-accent/40" />
              <h3 className="font-body text-[10px] tracking-[0.25em] text-ire-accent/50 uppercase">Household Setup</h3>
              <div className="flex gap-2">
                {["single", "married"].map((o) => (<button key={o} onClick={() => setHousehold(o)} className={`flex-1 py-3 font-body text-xs tracking-[0.15em] uppercase transition-all duration-400 border ${household === o ? btnOn : btnOff}`} data-testid={`toggle-${o}`}>{o === "single" ? "Single" : "Married / Splitting"}</button>))}
              </div>
              <AnimatePresence>{isMarried && (<motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden"><p className="font-body text-[10px] text-white/35 mb-2 tracking-wide">Income structure</p><div className="flex gap-2">{["one-income", "two-income"].map((o) => (<button key={o} onClick={() => setIncomeStructure(o)} className={`flex-1 py-2.5 font-body text-[10px] tracking-[0.12em] uppercase transition-all duration-400 border ${incomeStructure === o ? btnOn : btnOff}`} data-testid={`toggle-${o}`}>{o === "one-income" ? "One-income" : "Two-income"}</button>))}</div></motion.div>)}</AnimatePresence>
            </div>

            {/* Tax Profile */}
            <div className="relative bg-white/[0.015] border border-white/[0.08] p-6 space-y-5" data-testid="tax-profile">
              <div className="absolute top-0 right-0 w-8 h-px bg-ire-accent/40" /><div className="absolute top-0 right-0 w-px h-8 bg-ire-accent/40" />
              <h3 className="font-body text-[10px] tracking-[0.25em] text-ire-accent/50 uppercase">Your Tax Profile</h3>
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label className="font-body text-xs text-white/40">Annual gross income</label>
                  <motion.span key={grossIncome} initial={{ opacity: 0.5, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="font-heading text-2xl text-ire-accent font-light">{fmt(grossIncome)}</motion.span>
                </div>
                <div className="relative">
                  <div className="absolute top-1/2 left-0 right-0 h-[2px] -translate-y-1/2 bg-gradient-to-r from-ire-accent/5 via-ire-accent/20 to-ire-accent/5 rounded-full" />
                  <input type="range" min={40000} max={500000} step={5000} value={grossIncome} onChange={(e) => setGrossIncome(Number(e.target.value))} className="relative z-10 w-full h-[2px] bg-transparent appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-ire-accent [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_20px_rgba(212,175,106,0.5),0_0_40px_rgba(212,175,106,0.2)] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-ire-accent/80" data-testid="slider-gross-income" />
                </div>
                <div className="flex justify-between mt-2 mb-3"><span className="font-body text-[9px] text-white/25">€40K</span><span className="font-body text-[9px] text-white/25">€500K</span></div>
                <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 font-body text-sm text-white/30">€</span><input type="number" value={grossIncome} onChange={(e) => setGrossIncome(Math.max(0, Number(e.target.value)))} className="w-full bg-white/[0.02] border border-white/[0.08] pl-8 pr-4 py-2.5 font-body text-xs text-white/50 focus:outline-none focus:border-ire-accent/30 focus:shadow-[0_0_12px_rgba(212,175,106,0.1)] transition-all" data-testid="input-gross-income" /></div>
              </div>
              <AnimatePresence>{isMarried && isTwoIncome && (<motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden"><label className="font-body text-xs text-white/40 block mb-2">Spouse annual gross income</label><div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 font-body text-sm text-white/30">€</span><input type="number" value={spouseIncome} onChange={(e) => setSpouseIncome(Math.max(0, Number(e.target.value)))} className="w-full bg-white/[0.02] border border-white/[0.08] pl-8 pr-4 py-3 font-body text-sm text-white focus:outline-none focus:border-ire-accent/30 transition-all" data-testid="input-spouse-income" /></div></motion.div>)}</AnimatePresence>
              <div><label className="font-body text-[10px] text-white/45 block mb-2 tracking-wide">Employment type</label><div className="flex gap-2">{["employee", "self-employed"].map((o) => (<button key={o} onClick={() => setEmploymentType(o)} className={`flex-1 py-2.5 font-body text-[10px] tracking-[0.12em] uppercase transition-all duration-400 border ${employmentType === o ? btnOn : btnOff}`} data-testid={`toggle-${o}`}>{o === "employee" ? "Employee" : "Self-employed"}</button>))}</div></div>
              <div><label className="font-body text-[10px] text-white/45 block mb-2 tracking-wide">Church tax</label><div className="flex gap-2">{[false, true].map((o) => (<button key={String(o)} onClick={() => setChurchTax(o)} className={`flex-1 py-2.5 font-body text-[10px] tracking-[0.12em] uppercase transition-all duration-400 border ${churchTax === o ? btnOn : btnOff}`} data-testid={`toggle-church-${o}`}>{o ? "Yes" : "No"}</button>))}</div></div>
              <AnimatePresence>{churchTax && (<motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden"><label className="font-body text-[10px] text-white/45 block mb-2 tracking-wide">Federal state</label><select value={churchState} onChange={(e) => setChurchState(e.target.value)} className="w-full bg-white/[0.02] border border-white/[0.08] px-4 py-3 font-body text-sm text-white focus:outline-none focus:border-ire-accent/30 transition-all appearance-none cursor-pointer" data-testid="select-state">{Object.keys(CHURCH_TAX_STATES).map((s) => (<option key={s} value={s}>{s} ({CHURCH_TAX_STATES[s] * 100}%)</option>))}</select></motion.div>)}</AnimatePresence>
            </div>

            {/* PRECISION MODE — GENIUS MODE */}
            <div className={`relative overflow-hidden transition-all duration-700 border ${precisionMode ? "border-ire-accent/50 shadow-[0_0_40px_rgba(212,175,106,0.12),0_0_80px_rgba(212,175,106,0.05)]" : "precision-dormant border-ire-accent/15 bg-ire-accent/[0.02]"}`} data-testid="precision-mode">
              {/* Ambient corner glow when collapsed */}
              {!precisionMode && (<>
                <div className="absolute top-0 left-0 w-16 h-px bg-gradient-to-r from-ire-accent/40 to-transparent" />
                <div className="absolute top-0 left-0 w-px h-16 bg-gradient-to-b from-ire-accent/40 to-transparent" />
                <div className="absolute bottom-0 right-0 w-16 h-px bg-gradient-to-l from-ire-accent/40 to-transparent" />
                <div className="absolute bottom-0 right-0 w-px h-16 bg-gradient-to-t from-ire-accent/40 to-transparent" />
              </>)}
              {precisionMode && (<>
                <div className="absolute top-0 left-0 w-12 h-px bg-gradient-to-r from-ire-accent to-transparent" />
                <div className="absolute top-0 left-0 w-px h-12 bg-gradient-to-b from-ire-accent to-transparent" />
                <div className="absolute top-0 right-0 w-12 h-px bg-gradient-to-l from-ire-accent to-transparent" />
                <div className="absolute top-0 right-0 w-px h-12 bg-gradient-to-b from-ire-accent to-transparent" />
                <div className="absolute bottom-0 left-0 w-12 h-px bg-gradient-to-r from-ire-accent to-transparent" />
                <div className="absolute bottom-0 left-0 w-px h-12 bg-gradient-to-t from-ire-accent to-transparent" />
                <div className="absolute bottom-0 right-0 w-12 h-px bg-gradient-to-l from-ire-accent to-transparent" />
                <div className="absolute bottom-0 right-0 w-px h-12 bg-gradient-to-t from-ire-accent to-transparent" />
                <motion.div initial={{ top: 0 }} animate={{ top: "100%" }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }} className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-ire-accent/30 to-transparent pointer-events-none" />
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: `linear-gradient(rgba(212,175,106,1) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,106,1) 1px, transparent 1px)`, backgroundSize: "20px 20px" }} />
              </>)}
              <button onClick={() => setPrecisionMode(!precisionMode)} className={`relative w-full flex items-center justify-between px-6 py-5 text-left group z-10 ${precisionMode ? "bg-ire-accent/[0.03]" : ""}`}>
                <div className="flex items-center gap-4">
                  <div className={`relative w-10 h-10 flex items-center justify-center transition-all duration-700 ${precisionMode ? "shadow-[0_0_24px_rgba(212,175,106,0.35)]" : "shadow-[0_0_16px_rgba(212,175,106,0.12)]"}`}>
                    <div className={`absolute inset-0 border transition-all duration-700 ${precisionMode ? "border-ire-accent/60 bg-ire-accent/15 rotate-45 scale-90" : "border-ire-accent/30 bg-ire-accent/[0.06]"}`} />
                    <Crosshair className={`relative z-10 w-5 h-5 transition-all duration-700 ${precisionMode ? "text-ire-accent animate-pulse" : "text-ire-accent/50 group-hover:text-ire-accent/80"}`} />
                  </div>
                  <div>
                    <span className={`font-body text-xs tracking-[0.2em] uppercase block transition-all duration-500 ${precisionMode ? "text-ire-accent font-medium" : "text-ire-accent/50 group-hover:text-ire-accent/80"}`}>Precision Mode</span>
                    <span className={`font-body text-[9px] block mt-0.5 transition-all duration-500 ${precisionMode ? "text-ire-accent/40" : "text-ire-accent/25 group-hover:text-ire-accent/40"}`}>{precisionMode ? "Enhanced analysis active" : "Unlock genius-level estimates"}</span>
                  </div>
                </div>
                <div className={`w-8 h-8 flex items-center justify-center border transition-all duration-500 ${precisionMode ? "border-ire-accent/40 bg-ire-accent/10 shadow-[0_0_12px_rgba(212,175,106,0.2)]" : "border-ire-accent/20 bg-ire-accent/[0.04] hover:border-ire-accent/35"}`}>
                  {precisionMode ? <ChevronUp className="w-4 h-4 text-ire-accent" /> : <ChevronDown className="w-4 h-4 text-ire-accent/40" />}
                </div>
              </button>
              <AnimatePresence>{precisionMode && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.5 }} className="overflow-hidden relative z-10">
                  <div className="px-6 pb-6 space-y-5 border-t border-ire-accent/15 pt-5 bg-gradient-to-b from-ire-accent/[0.02] to-transparent">
                    <p className="font-body text-[11px] text-ire-accent/50 leading-relaxed">Unlock sharper estimates. These inputs refine your tax calculation and enable a more personalized strategic-fit assessment.</p>
                    <div><label className="font-body text-[10px] text-ire-accent/40 block mb-1.5 tracking-wide uppercase">Additional deductions</label><div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 font-body text-xs text-ire-accent/25">€</span><input type="number" value={deductions || ""} onChange={(e) => setDeductions(Math.max(0, Number(e.target.value)))} placeholder="0" className="w-full bg-ire-accent/[0.02] border border-ire-accent/10 pl-8 pr-4 py-2.5 font-body text-sm text-white focus:outline-none focus:border-ire-accent/30 focus:shadow-[0_0_12px_rgba(212,175,106,0.08)] transition-all placeholder:text-white/20" data-testid="input-deductions" /></div><p className="font-body text-[9px] text-white/25 mt-1">Werbungskosten / Sonderausgaben above standard allowance</p></div>
                    <div><label className="font-body text-[10px] text-ire-accent/40 block mb-1.5 tracking-wide uppercase">Annual bonus / variable</label><div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 font-body text-xs text-ire-accent/25">€</span><input type="number" value={bonus || ""} onChange={(e) => setBonus(Math.max(0, Number(e.target.value)))} placeholder="0" className="w-full bg-ire-accent/[0.02] border border-ire-accent/10 pl-8 pr-4 py-2.5 font-body text-sm text-white focus:outline-none focus:border-ire-accent/30 focus:shadow-[0_0_12px_rgba(212,175,106,0.08)] transition-all placeholder:text-white/20" data-testid="input-bonus" /></div></div>
                    <div><label className="font-body text-[10px] text-ire-accent/40 block mb-1.5 tracking-wide uppercase">Children</label><div className="flex gap-2">{[0,1,2,3,4].map((n) => (<button key={n} onClick={() => setChildren(n)} className={`w-10 h-10 font-body text-sm transition-all duration-300 border ${children === n ? "bg-ire-accent/15 border-ire-accent/50 text-ire-accent shadow-[0_0_12px_rgba(212,175,106,0.15)]" : "bg-transparent border-ire-accent/10 text-white/35 hover:border-ire-accent/25"}`} data-testid={`children-${n}`}>{n}</button>))}</div></div>
                    <div><label className="font-body text-[10px] text-ire-accent/40 block mb-1.5 tracking-wide uppercase">Available equity</label><div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 font-body text-xs text-ire-accent/25">€</span><input type="number" value={equity || ""} onChange={(e) => setEquity(Math.max(0, Number(e.target.value)))} placeholder="0" className="w-full bg-ire-accent/[0.02] border border-ire-accent/10 pl-8 pr-4 py-2.5 font-body text-sm text-white focus:outline-none focus:border-ire-accent/30 focus:shadow-[0_0_12px_rgba(212,175,106,0.08)] transition-all placeholder:text-white/20" data-testid="input-equity" /></div></div>
                    <div><label className="font-body text-[10px] text-ire-accent/40 block mb-1.5 tracking-wide uppercase">Existing properties</label><div className="flex gap-2">{[0,1,2,3,"4+"].map((n,i) => (<button key={i} onClick={() => setExistingProperties(typeof n === "string" ? 4 : n)} className={`flex-1 py-2.5 font-body text-[10px] transition-all duration-300 border ${existingProperties === (typeof n === "string" ? 4 : n) ? "bg-ire-accent/15 border-ire-accent/50 text-ire-accent shadow-[0_0_12px_rgba(212,175,106,0.15)]" : "bg-transparent border-ire-accent/10 text-white/35 hover:border-ire-accent/25"}`} data-testid={`properties-${n}`}>{n}</button>))}</div></div>
                    <div><label className="font-body text-[10px] text-ire-accent/40 block mb-1.5 tracking-wide uppercase">Investment goal</label><div className="grid grid-cols-2 gap-2">{[{value:"tax-first",label:"Tax optimization"},{value:"wealth-build",label:"Wealth building"},{value:"cash-flow",label:"Cash flow"},{value:"portfolio",label:"Portfolio growth"}].map((o) => (<button key={o.value} onClick={() => setGoalType(o.value)} className={`py-2.5 font-body text-[9px] tracking-[0.1em] uppercase transition-all duration-300 border ${goalType === o.value ? "bg-ire-accent/15 border-ire-accent/50 text-ire-accent shadow-[0_0_12px_rgba(212,175,106,0.15)]" : "bg-transparent border-ire-accent/10 text-white/35 hover:border-ire-accent/25"}`} data-testid={`goal-${o.value}`}>{o.label}</button>))}</div></div>
                  </div>
                </motion.div>
              )}</AnimatePresence>
            </div>

            {/* Scenario */}
            <div className="relative bg-white/[0.015] border border-white/[0.08] p-6 space-y-4" data-testid="scenario-selector">
              <div className="absolute bottom-0 right-0 w-8 h-px bg-ire-accent/40" /><div className="absolute bottom-0 right-0 w-px h-8 bg-ire-accent/40" />
              <h3 className="font-body text-[10px] tracking-[0.25em] text-ire-accent/50 uppercase">Scenario</h3>
              <p className="font-body text-[11px] text-white/30 leading-relaxed">These scenarios reflect different investment structures and tax-effect intensity. They are illustrative, not guaranteed.</p>
              <div className="flex gap-2">{["conservative", "typical", "strong"].map((s) => (<button key={s} onClick={() => setScenario(s)} className={`flex-1 py-3 font-body text-[10px] tracking-[0.12em] uppercase transition-all duration-400 border ${scenario === s ? (s === "strong" ? "bg-ire-accent/20 border-ire-accent/60 text-ire-accent shadow-[0_0_24px_rgba(212,175,106,0.2)]" : btnOn) : btnOff}`} data-testid={`scenario-${s}`}>{s}</button>))}</div>
              <p className="font-body text-[11px] text-white/25 italic">{scenarioDescriptions[scenario]}</p>
            </div>
          </motion.div>

          {/* RIGHT — Results */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="space-y-5">
            <div className="mb-2">
              <h2 className="font-heading text-2xl sm:text-3xl text-white font-extralight mb-1">Your estimated 2026 tax burden</h2>
              <p className="font-body text-xs text-white/35">Based on official 2026 German tax rules{precisionMode ? " and your precision inputs" : " and simplified assumptions"}</p>
            </div>

            {/* Summary */}
            <div className="relative bg-white/[0.02] border border-white/[0.08] p-6 overflow-hidden" data-testid="result-summary">
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-ire-accent/30 via-transparent to-ire-accent/30" />
              <p className="font-body text-base text-white/65 leading-relaxed">{isMarried ? `Your household is estimated to pay ${fmt(taxResult.totalTax)} in taxes in 2026.` : `You are estimated to pay ${fmt(taxResult.totalTax)} in taxes in 2026.`}</p>
              <p className="font-body text-sm text-ire-accent/60 mt-2">Now see how much of that could be strategically redirected into assets.</p>
              {precisionMode && (<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 mt-3 pt-3 border-t border-ire-accent/10"><div className="w-2 h-2 bg-ire-accent rounded-full animate-pulse shadow-[0_0_8px_rgba(212,175,106,0.4)]" /><span className="font-body text-[10px] text-ire-accent/50 tracking-[0.15em] uppercase">Precision Mode Active</span></motion.div>)}
            </div>

            {/* Primary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="primary-cards">
              <div className="relative bg-white/[0.02] border border-white/[0.08] p-5 overflow-hidden hover:border-white/15 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent" />
                <div className="absolute top-0 left-0 w-6 h-px bg-white/20" /><div className="absolute top-0 left-0 w-px h-6 bg-white/20" />
                <p className="relative font-body text-[10px] tracking-[0.15em] text-white/40 uppercase mb-3">Est. Total Tax Burden</p>
                <motion.p key={taxResult.totalTax} initial={{ opacity: 0.7 }} animate={{ opacity: 1 }} className="relative font-heading text-2xl sm:text-3xl text-white font-light tracking-tight">{fmt(taxResult.totalTax)}</motion.p>
              </div>
              <div className="relative p-5 overflow-hidden border border-ire-accent/30 hover:border-ire-accent/50 transition-all duration-500 bg-gradient-to-br from-ire-accent/[0.06] to-ire-accent/[0.02] shadow-[0_0_30px_rgba(212,175,106,0.08)]">
                <div className="absolute top-0 right-0 w-6 h-px bg-ire-accent/50" /><div className="absolute top-0 right-0 w-px h-6 bg-ire-accent/50" />
                <div className="absolute bottom-0 left-0 w-6 h-px bg-ire-accent/30" /><div className="absolute bottom-0 left-0 w-px h-6 bg-ire-accent/30" />
                <p className="relative font-body text-[10px] tracking-[0.15em] text-ire-accent/60 uppercase mb-3">Est. Year-One Recovery</p>
                <motion.p key={recoveryResult.recovery} initial={{ opacity: 0.7, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="relative font-heading text-2xl sm:text-3xl text-ire-accent font-light tracking-tight">{fmt(recoveryResult.recovery)}</motion.p>
              </div>
              <div className="relative bg-white/[0.02] border border-white/[0.08] p-5 overflow-hidden hover:border-white/15 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent" />
                <p className="relative font-body text-[10px] tracking-[0.15em] text-white/40 uppercase mb-3">Effective Tax Rate</p>
                <motion.p key={effectiveRate} initial={{ opacity: 0.7 }} animate={{ opacity: 1 }} className="relative font-heading text-2xl sm:text-3xl text-white font-light">{effectiveRate}<span className="text-lg text-white/40">%</span></motion.p>
              </div>
              <div className="relative bg-white/[0.02] border border-white/[0.08] p-5 overflow-hidden hover:border-white/15 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent" />
                <p className="relative font-body text-[10px] tracking-[0.15em] text-white/40 uppercase mb-3">Recovery Ratio</p>
                <motion.p key={recoveryResult.ratio} initial={{ opacity: 0.7 }} animate={{ opacity: 1 }} className="relative font-heading text-2xl sm:text-3xl text-white font-light">{recoveryResult.ratio}<span className="text-lg text-white/40">%</span></motion.p>
              </div>
              <div className="relative bg-white/[0.02] border border-white/[0.08] p-5 overflow-hidden hover:border-ire-accent/20 transition-all duration-500 sm:col-span-2">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent" />
                <p className="relative font-body text-[10px] tracking-[0.15em] text-white/40 uppercase mb-3">Likely Strategic Fit</p>
                <div className="relative flex items-center gap-2.5"><FitIcon className="w-5 h-5 text-ire-accent" /><p className="font-heading text-lg text-ire-accent font-light">{strategicFit.label}</p></div>
              </div>
            </div>

            {/* Fit Description */}
            <div className="relative bg-ire-accent/[0.03] border border-ire-accent/15 p-5 overflow-hidden" data-testid="strategic-fit-detail">
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-ire-accent/40 via-ire-accent/10 to-transparent" />
              <div className="flex items-start gap-3"><FitIcon className="w-5 h-5 text-ire-accent mt-0.5 shrink-0" /><div><p className="font-body text-xs text-ire-accent/80 font-medium mb-1">Best fit: {strategicFit.label}</p><p className="font-body text-sm text-white/45 leading-relaxed">{strategicFit.description}</p></div></div>
            </div>

            {/* Strategy View */}
            <div className="pt-4">
              <div className="flex items-center gap-4 mb-4"><div className="h-px w-8 bg-ire-accent/50" /><span className="font-body text-[10px] tracking-[0.3em] text-ire-accent/50 uppercase">IRE Strategy View</span><div className="h-px flex-1 bg-gradient-to-r from-ire-accent/15 to-transparent" /></div>
              <p className="font-body text-xs text-white/30 mb-3">Illustrative year-one tax recovery potential under an IRE-style investment structure</p>
            </div>

            {/* Secondary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="secondary-cards">
              {[{l:"Income Tax",v:fmt(taxResult.incomeTax),a:false},{l:"Social Contributions",v:fmt(taxResult.socialContributions),a:false},{l:"Soli + Church",v:fmt(taxResult.soli + taxResult.churchTaxAmount),a:false},{l:"Capital Direction",v:fmt(recoveryResult.recovery),a:true}].map((c,i) => (
                <div key={i} className="relative bg-white/[0.015] border border-white/[0.07] p-4 overflow-hidden"><div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-white/[0.05] to-transparent" /><p className="font-body text-[8px] tracking-[0.12em] text-white/30 uppercase mb-2">{c.l}</p><p className={`font-heading text-lg ${c.a ? "text-ire-accent/80" : "text-white/60"}`}>{c.v}</p></div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4" data-testid="cta-block">
              <button onClick={openCalendly} className="flex-1 group relative bg-ire-accent text-black py-4 font-body text-xs tracking-[0.2em] uppercase font-semibold hover:bg-white transition-all duration-500 flex items-center justify-center gap-3 overflow-hidden shadow-[0_0_30px_rgba(212,175,106,0.2)]" data-testid="cta-book-call"><span className="relative z-10">Book Strategy Call</span><ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform" /></button>
              <a href="#consultation" className="flex-1 border border-ire-accent/30 text-ire-accent py-4 font-body text-xs tracking-[0.2em] uppercase hover:bg-ire-accent/5 hover:border-ire-accent/50 transition-all duration-500 flex items-center justify-center gap-3" data-testid="cta-personalized">Get My Personalized Analysis</a>
            </div>

            {/* Assumptions */}
            <div className="border border-white/[0.06] mt-6" data-testid="assumptions-drawer">
              <button onClick={() => setShowAssumptions(!showAssumptions)} className="w-full flex items-center justify-between px-5 py-3"><div className="flex items-center gap-2"><Info className="w-3.5 h-3.5 text-white/25" /><span className="font-body text-[10px] tracking-[0.12em] text-white/30 uppercase">Calculation assumptions</span></div>{showAssumptions ? <ChevronUp className="w-3.5 h-3.5 text-white/25" /> : <ChevronDown className="w-3.5 h-3.5 text-white/25" />}</button>
              <AnimatePresence>{showAssumptions && (<motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden"><div className="px-5 pb-4 border-t border-white/[0.06] pt-3"><ul className="space-y-1.5 font-body text-[11px] text-white/30 leading-relaxed"><li>Tax year: 2026</li><li>Official German income tax tariff (§32a EStG, Grundfreibetrag €12,348)</li><li>Ehegattensplitting applied for married / joint filing</li><li>Solidarity surcharge: 2026 thresholds (€20,350 single / €40,700 married)</li><li>Church tax at state-specific rates (8% BW/BY, 9% all others)</li><li>Social contributions (employee share): Pension 9.3%, Health 7.3% + 1.45% avg. Zusatzbeitrag, Unemployment 1.3%, Care 1.8-2.4%</li><li>BBG 2026: Pension/Unemployment €101,400, Health/Care €69,750</li><li>Pflegeversicherung: childless 2.4%, 1 child 1.8%, 2 children 1.55%, 3+ reduced further</li><li>Werbungskostenpauschale €1,230 + Sonderausgaben €36/€72</li>{precisionMode && <li>Precision: Kinderfreibetrag €6,826 + BEA €2,928 per child applied to Soli + church tax</li>}{precisionMode && <li>Precision: Equity + properties inform strategic-fit assessment</li>}<li>Self-employed: no pension/unemployment, full health + care rate</li><li>IRE strategy outputs are illustrative, not guaranteed</li><li className="text-white/15 pt-1">Sources: BMF, Bundesregierung, GKV-Spitzenverband, DRV</li></ul></div></motion.div>)}</AnimatePresence>
            </div>

            {/* Disclaimer */}
            <div className="bg-white/[0.01] border border-white/[0.05] p-5 mt-4" data-testid="disclaimer">
              <p className="font-body text-[10px] tracking-[0.1em] text-white/25 uppercase mb-2">Important note</p>
              <p className="font-body text-[11px] text-white/30 leading-relaxed">This is an illustrative estimate based on official 2026 German tax rules and simplified assumptions. Final results depend on taxable income, deductions, family structure, church tax, payroll setup, and the actual investment structure.</p>
              <p className="font-body text-[10px] text-white/20 mt-3 italic">Built on 2026 German tax logic. Interpreted through IRE strategy logic.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorV2;

